import { apiClient } from '@lib/httpClient/apiClient';
import { addMedicationToFavoriteEndpoint } from '@lib/httpClient/endpoints/medications/addMedicationToFavoriteEndpoint';
import {
  getMedicationDetailsEndpoint,
  type IGetMedicationDetailsEndpointResponse,
} from '@lib/httpClient/endpoints/medications/getMedicationDetailsEndpoint';
import {
  type IListMedicationRoutesEndpointResponse,
  listMedicationRoutesEndpoint,
} from '@lib/httpClient/endpoints/medications/listMedicationRoutesEndpoint';
import {
  type IListMedicationsEndpointQuery,
  type IListMedicationsEndpointResponse,
  listMedicationsEndpoint,
} from '@lib/httpClient/endpoints/medications/listMedicationsEndpoint';
import { removeMedicationToFavoriteEndpoint } from '@lib/httpClient/endpoints/medications/removeMedicationToFavoriteEndpoint';

import type { IAddMedicationToFavoriteInputDTO } from './dtos/addToFavorites/IInputData';
import type { IGetMedicationDetailsInputDTO } from './dtos/getDetails/IInputData';
import type { IGetMedicationDetailsOutputDTO } from './dtos/getDetails/IOutputData';
import type { IListMedicationsInputDTO } from './dtos/list/IInputData';
import type { IListMedicationsOutputDTO } from './dtos/list/IOutputData';
import type { IListMedicationRoutesOutputDTO } from './dtos/listRoutes/IOutputData';
import type { IRemoveMedicationToFavoriteInputDTO } from './dtos/removeToFavorites/IInputData';
import type { IMedicationRepository } from './IMedicationRepository';

class MedicationService implements IMedicationRepository {
  public async addToFavorites(
    input: IAddMedicationToFavoriteInputDTO,
  ): Promise<void> {
    await apiClient.post({
      path: addMedicationToFavoriteEndpoint(input.id),
    });
  }

  public async getDetails(
    input: IGetMedicationDetailsInputDTO,
  ): Promise<IGetMedicationDetailsOutputDTO> {
    const response = await apiClient.get<IGetMedicationDetailsEndpointResponse>(
      {
        path: getMedicationDetailsEndpoint(input.id),
      },
    );
    const output: IGetMedicationDetailsOutputDTO = {
      info: {
        content: response.content,
        id: response.id,
        title: response.title,
      },
    };
    return output;
  }

  public async list(
    input: IListMedicationsInputDTO,
  ): Promise<IListMedicationsOutputDTO> {
    const response = await apiClient.get<
      IListMedicationsEndpointResponse,
      IListMedicationsEndpointQuery
    >({
      path: listMedicationsEndpoint(),
      params: {
        medication_route_id: input.medicationRouteId,
      },
    });
    const output: IListMedicationsOutputDTO = {
      list: response.list.map((item) => ({
        id: item.id,
        isFavorite: item.is_favorite,
        title: item.title,
      })),
    };
    return output;
  }

  public async listRoutes(): Promise<IListMedicationRoutesOutputDTO> {
    const response = await apiClient.get<IListMedicationRoutesEndpointResponse>(
      {
        path: listMedicationRoutesEndpoint(),
      },
    );
    const output: IListMedicationRoutesOutputDTO = {
      list: response.list.map((item) => ({
        id: item.id,
        title: item.title,
      })),
    };
    return output;
  }

  public async removeToFavorites(
    input: IRemoveMedicationToFavoriteInputDTO,
  ): Promise<void> {
    await apiClient.delete({
      path: removeMedicationToFavoriteEndpoint(input.id),
    });
  }
}

export const medicationService = new MedicationService();
